import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { useConditionalFreeze } from '@/domain/hooks/useConditionalFreeze'
import { RiskAcknowledgementInfo } from '@/domain/liquidation-risk-warning/types'
import { useLiquidationRiskWarning } from '@/domain/liquidation-risk-warning/useLiquidationRiskWarning'
import { InjectedActionsContext, Objective } from '@/features/actions/logic/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { UseFormReturn, useForm, useWatch } from 'react-hook-form'
import { getFormFieldsForWithdrawDialog, getWithdrawDialogFormValidator } from './form'
import { PositionOverview } from '@/domain/common/types/PositionOverview'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { IsolatedPairOverview } from '@/features/isolated-details/types'
import { calculateIsolatedHealthFactor, calculateUpdatedHealthFactor } from '@/domain/common/calculations/healthFactor'
import BigNumber from 'bignumber.js'

import {
  AssetInputSchema,
  DialogFormNormalizedData,
  UseDebouncedDialogFormValuesResult,
} from '@/features/dialogs/common/logic/form'
import { useDebounce } from '@/utils/useDebounce'
import { MyWalletProps } from '@/features/isolated-details/components/my-wallet/MyWallet'
import { track } from '@/features/analytics'

export interface UseDebouncedDialogFormValuesArgs {
  form: UseFormReturn<AssetInputSchema>
  walletOverview: MyWalletProps
}

export function useDebouncedDialogFormValues({
  form,
  walletOverview,
}: UseDebouncedDialogFormValuesArgs): UseDebouncedDialogFormValuesResult {
  const formValues = useWatch({ control: form.control })
  const isFormValid = form.formState.isValid

  const [keyCounter, setKeyCounter] = useState(0)
  const prevFormValuesRef = useRef(formValues)
  const prevIsFormValidRef = useRef(isFormValid)

  const normalizedFormValues = {
    token: walletOverview.withdraw?.token,
    value: NormalizedUnitNumber(formValues?.value || '0'),
    isMaxSelected: formValues?.isMaxSelected || false,
    position: {
      collateralBalance: NormalizedUnitNumber(0),
    },
    reserve: {
      supplyAPY: Percentage(0),
    },
  } as DialogFormNormalizedData

  // Update key when form values change
  useEffect(() => {
    const prevValues = prevFormValuesRef.current
    const prevIsValid = prevIsFormValidRef.current

    // Check for changes in important properties
    const hasChanged =
      prevValues?.symbol !== formValues?.symbol ||
      prevValues?.value !== formValues?.value ||
      prevValues?.isMaxSelected !== formValues?.isMaxSelected ||
      prevIsValid !== isFormValid

    if (hasChanged) {
      prevFormValuesRef.current = formValues
      prevIsFormValidRef.current = isFormValid
      setKeyCounter((prev) => prev + 1)
    }
  }, [formValues, isFormValid])

  const key = `form-change-${keyCounter}`

  const { debouncedValue, isDebouncing } = useDebounce({ formValues: normalizedFormValues, isFormValid }, key)

  return {
    debouncedFormValues: debouncedValue.formValues,
    isFormValid: debouncedValue.isFormValid,
    isDebouncing,
  }
}

export interface UseWithdrawDialogParams {
  walletOverview: MyWalletProps
  pairOverview?: IsolatedPairOverview
}

export interface UseWithdrawDialogResult {
  withdrawOptions: TokenWithBalance[]
  assetsToWithdrawFields: FormFieldsForDialog
  withdrawAsset: TokenWithValue
  objectives: Objective[]
  actionsContext: InjectedActionsContext
  pageStatus: PageStatus
  form: UseFormReturn<AssetInputSchema>
  currentPositionOverview: PositionOverview
  updatedPositionOverview?: PositionOverview
  riskAcknowledgement: RiskAcknowledgementInfo
}

export function useWithdrawDialog({ walletOverview, pairOverview }: UseWithdrawDialogParams): UseWithdrawDialogResult {
  const [pageStatus, setPageStatus] = useState<PageState>('form')

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(
      getWithdrawDialogFormValidator({ deposited: walletOverview.withdraw?.available || NormalizedUnitNumber(0) }),
      undefined,
      { raw: true },
    ),
    defaultValues: {
      symbol: walletOverview.withdraw?.token.symbol,
      value: '',
    },
    mode: 'onChange',
  })

  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedDialogFormValues({
    form,
    walletOverview,
  })
  const lastTrackedAmountKeyRef = useRef('')

  useEffect(() => {
    if (!formValues.token || formValues.value.eq(0)) {
      return
    }

    const key = `${formValues.token.address}-${formValues.value.toFixed()}`
    if (key === lastTrackedAmountKeyRef.current) {
      return
    }

    lastTrackedAmountKeyRef.current = key

    track('lend_borrow_amount_entered', {
      token: formValues.token.symbol,
      market: formValues.token.address,
      market_type: 'isolated',
      action_type: 'withdraw',
      amount: formValues.value.toFixed(),
    })
  }, [formValues])

  const withdrawAsset = useConditionalFreeze(formValues, pageStatus === 'success')

  const assetsToWithdrawFields = getFormFieldsForWithdrawDialog(
    form,
    walletOverview.withdraw!.token,
    walletOverview.withdraw!.available,
  )

  const collateralAmount = walletOverview.withdraw?.total || NormalizedUnitNumber(0)
  const borrowedAmount = walletOverview.repay?.available || NormalizedUnitNumber(0)

  let currentHealthFactor = new BigNumber(0)
  let updatedHealthFactor = new BigNumber(0)

  if (pairOverview) {
    const maxLtv = pairOverview.maxLtv
    const normalizedExchangeRate = pairOverview.normalizedExchangeRate

    currentHealthFactor = calculateIsolatedHealthFactor(
      collateralAmount,
      normalizedExchangeRate,
      borrowedAmount,
      maxLtv,
    )

    const withdrawalAmount = withdrawAsset.value
    const remainingCollateral = collateralAmount.gt(withdrawalAmount)
      ? NormalizedUnitNumber(collateralAmount.toNumber() - withdrawalAmount.toNumber())
      : NormalizedUnitNumber(0)

    if (remainingCollateral.eq(0) && borrowedAmount.toNumber() > 0) {
      // no collateral but with debt => zero health factor (underwater)
      updatedHealthFactor = new BigNumber(0)
    } else {
      updatedHealthFactor = calculateUpdatedHealthFactor({
        collateralAmount,
        collateralExchangeRate: normalizedExchangeRate,
        borrowedAmount,
        maxLtv,
        collateralChange: withdrawalAmount.toNumber(),
        isCollateralIncrease: false,
        isIsolated: true,
      })
    }
  }

  const currentPositionOverview: PositionOverview = {
    healthFactor: currentHealthFactor,
    tokenSupply: collateralAmount,
    supplyAPY: withdrawAsset.reserve.supplyAPY,
  }

  const updatedPositionOverview = withdrawAsset.value.eq(0)
    ? undefined
    : ({
        ...currentPositionOverview,
        healthFactor: updatedHealthFactor,
        tokenSupply: pairOverview
          ? NormalizedUnitNumber(Math.max(0, collateralAmount.toNumber() - withdrawAsset.value.toNumber()))
          : NormalizedUnitNumber(Math.max(0, collateralAmount.toNumber() - withdrawAsset.value.toNumber())),
      } as PositionOverview)

  const { riskAcknowledgement, disableActionsByRisk } = useLiquidationRiskWarning({
    type: 'liquidation-warning-withdraw',
    isFormValid,
    currentHealthFactor: currentPositionOverview.healthFactor,
    updatedHealthFactor: updatedPositionOverview?.healthFactor,
  })

  const actionsEnabled = withdrawAsset.value.gt(0) && isFormValid && !isDebouncing && !disableActionsByRisk

  return {
    form,
    withdrawOptions: [{ token: walletOverview.withdraw!.token, balance: walletOverview.withdraw!.available }],
    assetsToWithdrawFields,
    withdrawAsset,
    objectives: [
      {
        type: 'isolatedWithdraw',
        token: walletOverview.withdraw!.token,
        value: withdrawAsset.value,
      },
    ],
    actionsContext: {
      isolatedInfo: {
        pairAddress: walletOverview.pairAddress,
      },
    },
    pageStatus: {
      actionsEnabled,
      state: pageStatus,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    currentPositionOverview,
    updatedPositionOverview,
    riskAcknowledgement,
  }
}
